import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { FilterOp, NumericFilterDef, NumericFilterValue } from '../filterTypes';
import { FILTER_OPERATOR_OPTIONS } from '../filterTypes';
import type { FilterFieldProps } from '../FilterField.types';
import { FieldLabel, FieldError } from '../FilterFieldHelpers';
import { validateFilterValue } from '../filterValidation';
import { textFieldSx } from '../styles/filterColumnSx';
import '../styles/filterPanel.css';

export function NumericFilterInput({
  def,
  value,
  onChange,
  onClear,
  variant = 'panel',
  label,
  op = 'eq',
  onOpChange,
}: FilterFieldProps) {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const isColumn = variant === 'column';
  const numVal = value as NumericFilterValue | undefined;
  const currentValue = numVal != null ? String(numVal.value) : '';
  const hasValue = currentValue !== '';

  // Native min/max come from the column's filter def (e.g. activity rate 0–100).
  const inputProps = (def as NumericFilterDef | undefined)?.inputProps;

  // Inline validation (message only — the invalid value is not sent to the
  // backend: appendFilterParams skips it via the same validator).
  const error = validateFilterValue(def, numVal);
  const errorText = error
    ? t(`table.filter.error.${error.key}`, error.params)
    : undefined;

  if (isColumn) {
    return (
      <TextField
        size="small"
        fullWidth
        type="number"
        value={currentValue}
        error={!!error}
        onChange={e => {
          if (!e.target.value) onClear();
          else onChange({ kind: 'numeric', value: Number(e.target.value), op: 'eq' });
        }}
        sx={textFieldSx(theme)}
        slotProps={{
          htmlInput: { ...inputProps },
          input: {
            ...(hasValue && {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={onClear}
                    edge="end"
                    tabIndex={-1}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: '0.8rem' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }),
          },
        }}
      />
    );
  }

  return (
    <Box className="filter-field">
      {label && <FieldLabel>{label}</FieldLabel>}
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'stretch', height: '100%' }}>
        <FormControl size="small" sx={{ width: 64, flexShrink: 0 }}>
          <Select
            value={op}
            onChange={e => onOpChange?.(e.target.value as FilterOp)}
            className="filter-select"
          >
            {FILTER_OPERATOR_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value} className="filter-menu-item-sm">
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          fullWidth
          type="number"
          value={currentValue}
          placeholder="—"
          error={!!error}
          onChange={e => {
            if (!e.target.value) onClear();
            else onChange({ kind: 'numeric', value: Number(e.target.value), op });
          }}
          className="filter-input"
          slotProps={{ htmlInput: { ...inputProps } }}
        />
        {value && (
          <IconButton size="small" onClick={onClear} className="filter-clear-btn">
            <ClearIcon className="filter-clear-icon" />
          </IconButton>
        )}
      </Box>
      {errorText && <FieldError>{errorText}</FieldError>}
    </Box>
  );
}
